import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAppServices } from "../../../../../../hook/services";

const CreateCategory = ({ handleRefresh, data, languageId }) => {
  const AppService = useAppServices();
  const { companyId } = useParams();

  const isEdit = data?._id;
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    shortDescription: "",
  });

  useEffect(() => {
    if (isEdit) {
      setFormData(data);
    }
  }, [isEdit, data]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      companyId: companyId,
      languageId: languageId,
      createdBy: "AGENCY",
      ...(data?._id && { _id: data._id }),
    };

    const { response } = await AppService?.knowledgebase_category?.[
      data?._id ? "update" : "create"
    ]({
      payload,
    });

    if (response) {
      handleRefresh();
      toast.success(isEdit ? `Category updated!` : `Category added!`);
      handleClose();
    } else {
      toast.error(`Error saving Category`);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    const result = await Swal.fire({
      title: "Delete this category?",
      text: "All articles inside this category will also be permanently deleted. This action cannot be undone.",
      iconHtml:
        '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
      showCancelButton: true,
      confirmButtonText: "Delete category",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "cc-swal-popup",
        icon: "cc-swal-icon",
        title: "cc-swal-title",
        htmlContainer: "cc-swal-text",
        actions: "cc-swal-actions",
        confirmButton: "cc-swal-btn cc-swal-btn--danger",
        cancelButton: "cc-swal-btn cc-swal-btn--ghost",
      },
    });

    if (!result.isConfirmed) return;

    const { response } = await AppService?.knowledgebase_category.delete({
      query: `_id=${data?._id}`,
    });

    if (response) {
      handleRefresh();
      toast.success("Category deleted!");
      handleClose();
    } else {
      toast.error(`Error deleting Category`);
    }
  };

  return (
    <>
      {isEdit ? (
        <button
          type="button"
          className="cc-icon-btn"
          title="Edit"
          onClick={handleOpen}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className="cc-trigger d-inline-flex align-items-center"
          onClick={handleOpen}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create category
        </button>
      )}

      {open && (
        <div className="cc-overlay" onMouseDown={handleClose}>
          <div
            className="cc-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              <header className="cc-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="cc-title mb-0">
                    {isEdit ? "Edit category" : "Create category"}
                  </h5>
                  <p className="cc-subtitle mb-0">
                    {isEdit
                      ? "Update the category name or description."
                      : "Add a new category to organize your articles."}
                  </p>
                </div>
                <button
                  type="button"
                  className="cc-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </header>

              <div className="cc-body">
                <div className="cc-field">
                  <label className="cc-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="cc-input"
                    placeholder="e.g. Getting started"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="cc-field mb-0">
                  <label className="cc-label">Short description</label>
                  <input
                    type="text"
                    name="shortDescription"
                    className="cc-input"
                    placeholder="Briefly describe this category"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <footer className="cc-footer d-flex justify-content-end gap-2">
                {isEdit && (
                  <button
                    type="button"
                    className="cc-btn cc-btn--danger me-auto"
                    onClick={handleDelete}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                    </svg>
                    Delete
                  </button>
                )}
                <button type="submit" className="cc-btn cc-btn--primary">
                  {isEdit ? "Save changes" : "Create category"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      <style>{`
        /* Trigger */
        .cc-trigger {
          font-family: 'Inter', sans-serif;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #1f2937;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .cc-trigger:hover {
          background: #fafafa;
          border-color: #d1d5db;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .cc-trigger:active { transform: translateY(0); }
        .cc-trigger:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        .cc-icon-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          border-radius: 7px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
          transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .cc-icon-btn:hover {
          background: #fafafa;
          border-color: #d1d5db;
          color: #1f2937;
          transform: translateY(-1px);
        }
        .cc-icon-btn:active { transform: translateY(0); }
        .cc-icon-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        /* Overlay & dialog */
        .cc-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          font-family: 'Inter', sans-serif;
          animation: ccFade 0.18s ease;
        }
        @keyframes ccFade { from { opacity: 0; } to { opacity: 1; } }
        .cc-dialog {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #ececec;
          width: 100%;
          max-width: 480px;
          max-height: calc(100vh - 48px);
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
          animation: ccPop 0.18s ease;
          overflow: hidden;
        }
        @keyframes ccPop {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .cc-header { padding: 18px 22px 14px; }
        .cc-title {
          font-size: 1.0625rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }
        .cc-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-top: 2px !important;
        }
        .cc-close {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: transparent;
          border: 0;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .cc-close:hover { background: #f3f4f6; color: #1f2937; }
        .cc-body { padding: 6px 22px 18px; overflow-y: auto; }
        .cc-footer {
          padding: 14px 22px 18px;
          border-top: 1px solid #f1f1f1;
          background: #fff;
        }

        /* Fields */
        .cc-field { margin-bottom: 16px; }
        .cc-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .cc-input {
          width: 100%;
          height: 38px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          font-family: inherit;
        }
        .cc-input::placeholder { color: #9ca3af; }
        .cc-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        /* Buttons */
        .cc-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .cc-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .cc-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .cc-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .cc-btn--primary:active { transform: translateY(0); }
        .cc-btn--danger {
          background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(185, 28, 28, 0.22),
            0 4px 10px rgba(220, 38, 38, 0.2);
        }
        .cc-btn--danger:hover {
          background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(153, 27, 27, 0.28),
            0 8px 18px rgba(220, 38, 38, 0.3);
        }
        .cc-btn--danger:active { transform: translateY(0); }

        /* SweetAlert delete confirmation */
        .cc-swal-popup {
          border-radius: 14px !important;
          border: 1px solid #ececec;
          font-family: 'Inter', sans-serif !important;
          padding: 6px 4px 14px !important;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18) !important;
          width: 420px !important;
        }
        .swal2-icon.cc-swal-icon {
          width: 56px !important;
          height: 56px !important;
          border-width: 1px !important;
          border-color: #fecaca !important;
          background: #fef2f2 !important;
          color: #dc2626 !important;
          margin: 18px auto 6px !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .swal2-icon.cc-swal-icon .swal2-icon-content {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0;
        }
        .swal2-icon.cc-swal-icon .swal2-icon-content svg { stroke: #dc2626; }
        .cc-swal-title {
          font-size: 1.0625rem !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          letter-spacing: -0.005em;
          padding: 4px 16px 0 !important;
          margin: 0 !important;
        }
        .cc-swal-text {
          font-size: 0.875rem !important;
          color: #6b7280 !important;
          line-height: 1.5 !important;
          padding: 8px 24px 0 !important;
          margin: 0 !important;
        }
        .cc-swal-actions {
          margin: 22px 16px 4px !important;
          gap: 8px !important;
          justify-content: flex-end !important;
        }
        .cc-swal-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.005em;
          padding: 9px 16px !important;
          border-radius: 9px !important;
          border: 1px solid transparent !important;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
          margin: 0 !important;
        }
        .cc-swal-btn:focus {
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.18) !important;
        }
        .cc-swal-btn--ghost {
          background: #fff !important;
          border-color: #e5e7eb !important;
          color: #1f2937 !important;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04) !important;
        }
        .cc-swal-btn--ghost:hover {
          background: #fafafa !important;
          border-color: #d1d5db !important;
          transform: translateY(-1px);
        }
        .cc-swal-btn--ghost:active { transform: translateY(0); }
        .cc-swal-btn--danger {
          background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%) !important;
          color: #fff !important;
          border-color: rgba(255, 255, 255, 0.04) !important;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(185, 28, 28, 0.22),
            0 4px 10px rgba(220, 38, 38, 0.2) !important;
        }
        .cc-swal-btn--danger:hover {
          background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(153, 27, 27, 0.28),
            0 8px 18px rgba(220, 38, 38, 0.3) !important;
        }
        .cc-swal-btn--danger:active { transform: translateY(0); }
      `}</style>
    </>
  );
};

export default CreateCategory;
